import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export const ErrorFallback = ({
  error,
  componentStack,
  scope,
}: {
  error: Error;
  componentStack: string;
  scope: "page" | "application";
}) => {
  const copyComponentStack = async (
    errorCopy: string,
    componentStackCopy: string
  ) => {
    try {
      await navigator.clipboard.writeText(
        `${errorCopy}\n${componentStackCopy}`
      );
      toast.success("Component stack copied to clipboard");
    } catch {
      toast.error("Failed to copy component stack");
    }
  };

  const timestamp = new Date().toLocaleString();
  const errorAsString = error.toString();

  const titles = {
    page: "Page Error",
    application: "Application Error",
  };

  const descriptions = {
    page: "An error occurred while rendering this page. The error has been logged and our team will investigate.",
    application:
      "An unexpected error occurred in the application. Please try refreshing the page.",
  };

  return (
    <div className="flex min-h-[400px] items-center justify-center p-4">
      <Card className="mx-auto mt-8 max-w-2xl">
        <CardHeader>
          <CardTitle className="text-xl">{titles[scope]}</CardTitle>
          <CardDescription>{descriptions[scope]}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-destructive-foreground">{errorAsString}</p>
          <p className="-mt-2 text-destructive-foreground/50 text-sm">
            {timestamp}
          </p>
          <div className="w-full overflow-x-auto rounded-md border border-border p-4">
            <pre className="font-mono text-xs">{componentStack}</pre>
          </div>
          <Button
            onClick={() => copyComponentStack(errorAsString, componentStack)}
          >
            Copy Error Details
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
